/**
 * RenderLoop - Manages the game rendering loop
 * with performance optimizations and FPS throttling
 */
export class RenderLoop {
    constructor(renderer, scene, camera, callback) {
        this.renderer = renderer;
        this.scene = scene;
        this.camera = camera;
        this.callback = callback;
        this.isRunning = false;
        this.animationFrameId = null;
        
        // Performance tracking
        this.fps = 60;
        this.targetFps = 60;
        this.lastFrameTime = 0;
        this.frameCount = 0;
        this.frameTimeAccumulator = 0;
        this.frameTimeWindow = 1000; // 1 second window for FPS calculation
        this.lastFpsUpdateTime = 0;
        
        // Mobile detection for performance optimizations
        this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (this.isMobile) {
            this.targetFps = 30; // Lower target FPS on mobile for better performance
        }
        
        // Bind method to maintain this context
        this.loop = this.loop.bind(this);
    }
    
    /**
     * Start the render loop
     */
    start() {
        if (this.isRunning) return;
        
        console.log('Starting render loop');
        this.isRunning = true;
        this.lastFrameTime = performance.now();
        this.lastFpsUpdateTime = this.lastFrameTime;
        this.frameCount = 0;
        this.frameTimeAccumulator = 0;
        this.animationFrameId = requestAnimationFrame(this.loop);
    }
    
    /**
     * Stop the render loop
     */
    stop() {
        if (!this.isRunning) return;
        
        console.log('Stopping render loop');
        this.isRunning = false;
        
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }
    
    /**
     * Main loop function
     * @param {number} timestamp - Current time from requestAnimationFrame
     */
    loop(timestamp) {
        if (!this.isRunning) return;
        
        // Calculate time delta
        const deltaTime = timestamp - this.lastFrameTime;
        
        // Skip frame if we're rendering faster than our target FPS
        // This prevents burning CPU/GPU on higher refresh rate displays
        if (deltaTime < (1000 / this.targetFps)) {
            this.animationFrameId = requestAnimationFrame(this.loop);
            return;
        }
        
        // FPS calculation
        this.frameCount++;
        this.frameTimeAccumulator += deltaTime;
        
        // Update FPS counter once per second
        if (timestamp - this.lastFpsUpdateTime > this.frameTimeWindow) {
            this.fps = Math.round((this.frameCount * 1000) / this.frameTimeAccumulator);
            
            // Reset accumulators
            this.frameCount = 0;
            this.frameTimeAccumulator = 0;
            this.lastFpsUpdateTime = timestamp;
            
            // Output FPS to console occasionally
            console.log(`FPS: ${this.fps}`);
        }
        
        // Store current time for next frame
        this.lastFrameTime = timestamp;
        
        // Call the update callback with delta time (in seconds)
        if (this.callback) {
            this.callback(deltaTime / 1000);
        }
        
        // Render the scene
        if (this.renderer && this.scene && this.camera) {
            this.renderer.render(this.scene, this.camera);
        } else {
            console.warn('RenderLoop: Missing renderer, scene, or camera');
        }
        
        // Request next frame
        this.animationFrameId = requestAnimationFrame(this.loop);
    }
    
    /**
     * Set the target FPS
     * @param {number} targetFps - Target frames per second
     */
    setTargetFps(targetFps) {
        this.targetFps = Math.max(1, Math.min(120, targetFps));
        console.log(`Target FPS set to ${this.targetFps}`);
    }
    
    /**
     * Get the current FPS
     * @returns {number} Current frames per second
     */
    getFps() {
        return this.fps;
    }
    
    /**
     * Check if the render loop is currently running
     * @returns {boolean} True if running
     */
    isActive() {
        return this.isRunning;
    }
} 